/**
 * The vocabularies the ecosystem maps are built on.
 *
 * These mirror `startupfuel/models/ecosystem_submission.ts` exactly — the API
 * validates against its own copy and rejects anything outside it, so a value
 * that drifts here surfaces as a field error rather than a silent mismatch.
 * Duplicated rather than shared because the two repos have no common package;
 * that is the same trade `src/lib/gics-level5.json` already makes.
 */

export const ECOSYSTEM_PLAYER_TYPES = [
  "VC Fund",
  "Angel Network",
  "Accelerator / Incubator",
  "Technology Company",
  "Research / University",
  "Government / Public Funding",
  "Corporate Innovation",
  "Ecosystem Organization",
] as const;

export const ECOSYSTEM_FUNDING_MODELS = [
  "Public Funded",
  "Private",
  "Public Markets",
  "Corporate Funded",
  "Public / Academic",
  "Mixed / Nonprofit",
  "Mixed / Private",
  "Mixed Public–Private",
] as const;

export const ECOSYSTEM_OWNERSHIP_TYPES = [
  "Public Company",
  "Private Company",
  "Corporate Owned",
] as const;

/**
 * How many GICS sub-industries one submission may claim. Mirrors
 * MAX_GICS_SUB_INDUSTRIES on the backend, which rejects anything past it.
 */
export const MAX_GICS_SUB_INDUSTRIES = 5;

export const ECOSYSTEM_REGIONS = [
  "Ontario",
  "Québec",
  "British Columbia",
  "Alberta",
  "Saskatchewan",
  "Manitoba",
  "Nova Scotia",
  "New Brunswick",
  "Prince Edward Island",
  "Newfoundland & Labrador",
  "Yukon",
  "Northwest Territories",
  "Nunavut",
  "National",
] as const;

/**
 * The maps that accept submissions, keyed by the segment in the URL
 * (/ecosystem/<key>/submit). One today; the page 404s on anything else rather
 * than filing a record against a map that doesn't exist.
 */
export const ECOSYSTEMS: Record<
  string,
  { key: string; label: string; tagline: string; defaultCountry: string }
> = {
  canada: {
    key: "canada",
    label: "Canada",
    tagline: "An undercover innovation superpower",
    defaultCountry: "Canada",
  },
};

export type EcosystemPlayerType = (typeof ECOSYSTEM_PLAYER_TYPES)[number];
export type EcosystemFundingModel = (typeof ECOSYSTEM_FUNDING_MODELS)[number];
export type EcosystemOwnershipType = (typeof ECOSYSTEM_OWNERSHIP_TYPES)[number];
export type EcosystemRegion = (typeof ECOSYSTEM_REGIONS)[number];

/** Where the public map lives, for the "back to the map" links. */
export const ecosystemMapUrl = (key: string): string =>
  `https://www.startupfuel.com/ecosystem/${key}`;
